import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { Button, Divider, Grid, Icon, Link, SectionHeader } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import RouteKey from '../../../../routes/key';
import { candidateService } from '../../../../api';
import ContactDetails from '../../../../components/ContactDetails';
import PersonalDetails from '../../../../components/PersonalDetails';
import ReceiptDetails from '../../../../components/ReceiptDetails';
import SelectedSubjectsReview from '../../../../components/SelectedSubjectsReview';
import { parseLocationSearch } from '../../../../utils/StringUtils';

const { amendment } = RouteKey.child;
const { examRegistrations } = RouteKey.amendment.child;

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    marginRight: theme.spacing(2),
  },
}));

interface ResitSummaryParams {
  internalTransactionRef?: string;
}

const ResitSummary = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();

  const location = useLocation<ResitSummaryParams>();
  const { internalTransactionRef } = parseLocationSearch(location.search);

  /** to get resit payment transaction
   * */
  const { data } = candidateService.resit.candidate.useGetCandidateResitTransaction(
    internalTransactionRef,
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: true,
    }
  );

  /** to get resit summary
   * */
  const getResitSummary = candidateService.resit.candidate.useResitReceiptSummary(
    internalTransactionRef,
    {
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const { refetch, isFetching } = candidateService.resit.candidate.useCandidateDownloadSummary(
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    internalTransactionRef
  );

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          <SectionHeader
            title={t('resit->>ResitSummary.Receipt')}
          >
            {isFetching ? (
                <Icon
                    name={IconName.loadingIndicator}
                    size="L"
                    color={theme.color.link.linkActive02}
                />
            ) : (
                <Link id="link-download" onClick={() => refetch()}>
                  Download Summary
                </Link>
            )}
          </SectionHeader>
        </Grid>

        <Grid xs={12}>
          <ReceiptDetails
            transaction={{
              transactionNumber: data?.transactionNumber || '',
              internalTransactionRef: data?.internalTransactionRef,
              totalFee: data?.totalFee || '',
              paymentMethod: data?.paymentMethod || '',
              paymentTimeStamp: data?.paymentTimeStamp || '',
              gstRegNo: getResitSummary?.data?.subjectSummaryModel.gstRegNo,
              examLevel: data?.examLevel,
              examYear: data?.examYear,
            }}
          />
        </Grid>
      </Grid>

      <Divider />

      <Grid container>
        <Grid xs={12}>
          <PersonalDetails
            statutoryName={getResitSummary?.data?.candidateModel.statutoryName}
            hanyuPinyinName={getResitSummary?.data?.candidateModel.hanyuPinyinName}
            nricFin={getResitSummary?.data?.candidateModel.nricFin}
            nationality={getResitSummary?.data?.candidateModel.nationality}
            citizenship={getResitSummary?.data?.candidateModel.citizenship}
            schoolName={getResitSummary?.data?.candidateModel.schoolName}
          />
        </Grid>
      </Grid>

      <Divider />

      <Grid container>
        <Grid xs={12}>
          <ContactDetails contactDetails={getResitSummary?.data?.candidateModel} editable={false} />
        </Grid>
      </Grid>

      <Divider />

      <Grid container>
        <Grid xs={12}>
          <SelectedSubjectsReview
            candidateSubjects={getResitSummary?.data?.subjectSummaryModel.subjectInfo}
            examLevel={getResitSummary?.data?.subjectSummaryModel.examLevel}
            examYear={getResitSummary?.data?.subjectSummaryModel.examYear}
            gstRegNo={getResitSummary?.data?.subjectSummaryModel.gstRegNo}
            totalExamFee={getResitSummary?.data?.subjectSummaryModel.totalExamFee}
            totalGst={getResitSummary?.data?.subjectSummaryModel.totalGst}
            totalFee={getResitSummary?.data?.subjectSummaryModel.totalFee}
          />
        </Grid>

        <Grid xs={12}>
          <Button
            id="button-resitSummary-goToRegisteredExams"
            className={classes.button}
            onClick={() => history.push(`/${amendment}/${examRegistrations.route}`)}
          >
            {t('resit->>ResitSummary.View Registered Exams')}
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default ResitSummary;
